import { E as EmblaCarousel } from "./embla-carousel.esm-3bCdUmQG.js";
import { F as Fade } from "./embla-carousel-fade.esm-CqGaA1Zm.js";
import { C as ClassNames } from "./embla-carousel-class-names.esm-QFCgdsfc.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { e as extractSlideColorSchemes, d as duplicateSlidesForLoop, f as addLayoutShiftPrevention, a as addPrevNextBtnsClickHandlersSSR, b as addDotsBtnsAndClickHandlersSSR, g as addColorSchemeSyncHandlers } from "./embla-extension-OubijMX1.js";
import { r as requestIdleCallback } from "./utilities-2Suih3Nm.js";
class ArticleSlideshow extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.originalSlideCount = 0;
    this.slideColorSchemes = [];
    this.cleanupFunctions = [];
    this.nodes = {
      embla: null,
      container: null,
      viewport: null,
      prevBtn: null,
      nextBtn: null,
      slideshowDots: null,
      paginationContainer: null
    };
    this.config = {
      standardEffect: "fade",
      showNavigation: false,
      showSlideshowDots: false,
      autoPlay: false,
      options: {}
    };
  }
  connectedCallback() {
    this.cacheNodes();
    this.parseConfig();
    if (Shopify.designMode) {
      this.init();
    } else {
      requestIdleCallback(() => this.init());
    }
  }
  disconnectedCallback() {
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    if (!this.nodes.embla) {
      throw new Error("SlideshowV2: .embla element not found");
    }
    this.nodes.container = this.nodes.embla.querySelector(".embla__container");
    if (!this.nodes.container) {
      throw new Error("SlideshowV2: .embla__container element not found");
    }
    this.nodes.viewport = this.nodes.embla.querySelector(".embla__viewport");
    if (!this.nodes.viewport) {
      throw new Error("SlideshowV2: .embla__viewport element not found");
    }
    this.nodes.prevBtn = this.nodes.embla.querySelector(".embla__snip-button--prev");
    this.nodes.nextBtn = this.nodes.embla.querySelector(".embla__snip-button--next");
    this.nodes.slideshowDots = this.nodes.embla.querySelector(".embla__snip-dots");
    this.nodes.paginationContainer = this.nodes.embla.querySelector(".featured-article-pagination");
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showSlideshowDots = this.dataset.showSlideshowDots === "true";
    this.config.standardEffect = this.dataset.animStandardEffect || "fade";
    this.config.options = this.parseJSON(this.dataset.options, {});
    this.config.autoPlay = this.parseJSON(this.dataset.autoPlay, false) || false;
  }
  parseJSON(jsonString, fallback) {
    if (!jsonString || !jsonString.trim()) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(jsonString.replace(/,\s*([}\]])/g, "$1"));
      if (typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
      return fallback;
    } catch (error) {
      return fallback;
    }
  }
  // ========== Initialization ==========
  init() {
    if (this.emblaApi) {
      this.destroy();
    }
    const slideCount = this.nodes.container.children.length;
    if (slideCount <= 1) {
      this.handleSingleSlide(slideCount);
      return;
    }
    try {
      const plugins = this.initPlugins();
      this.emblaApi = EmblaCarousel(this.nodes.viewport, this.config.options, plugins);
      this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
      this.originalSlideCount = this.nodes.container.children.length;
      if (this.config.options.loop) {
        this.originalSlideCount = duplicateSlidesForLoop(this.emblaApi, this.nodes.container);
      }
      this.setupFeatures();
      this.nodes.embla.classList.add("is-initialized");
    } catch (error) {
      throw error;
    }
  }
  handleSingleSlide(slideCount) {
    this.slideColorSchemes = extractSlideColorSchemes(this.nodes.container);
    this.originalSlideCount = slideCount;
    if (this.nodes.paginationContainer && this.slideColorSchemes[0]) {
      this.nodes.paginationContainer.classList.add(this.slideColorSchemes[0]);
    }
    this.nodes.embla.classList.add("is-initialized");
  }
  initPlugins() {
    const plugins = [];
    plugins.push(ClassNames({
      snapped: "is-snapped",
      inView: ""
    }));
    if (this.config.autoPlay) {
      plugins.push(Autoplay(this.config.autoPlay));
    }
    plugins.push(Fade());
    return plugins;
  }
  setupFeatures() {
    const layoutShiftCleanup = addLayoutShiftPrevention(this.emblaApi, this.nodes.embla);
    this.registerCleanup(layoutShiftCleanup);
    if (this.config.showNavigation) {
      this.enableNavigation();
    }
    if (this.config.showSlideshowDots) {
      this.enableSlideshowDots();
    }
    this.enableColorSchemeSync();
  }
  // ========== Features ==========
  enableNavigation() {
    const { prevBtn, nextBtn } = this.nodes;
    if (!prevBtn || !nextBtn || !this.emblaApi) return;
    const cleanup = addPrevNextBtnsClickHandlersSSR(this.emblaApi, prevBtn, nextBtn);
    this.registerCleanup(cleanup);
  }
  enableSlideshowDots() {
    const node = this.nodes.slideshowDots;
    if (!node || !this.emblaApi) return;
    const originalCount = this.originalSlideCount > 0 ? this.originalSlideCount : null;
    const cleanup = addDotsBtnsAndClickHandlersSSR(this.emblaApi, node, originalCount);
    this.registerCleanup(cleanup);
  }
  // ========== Color Scheme Sync ==========
  enableColorSchemeSync() {
    const cleanup = addColorSchemeSyncHandlers(
      this.emblaApi,
      this.nodes.paginationContainer,
      this.slideColorSchemes,
      this.originalSlideCount
    );
    this.registerCleanup(cleanup);
  }
  // ========== Cleanup ==========
  registerCleanup(cleanup) {
    if (cleanup && typeof cleanup === "function") {
      this.cleanupFunctions.push(cleanup);
    }
  }
  destroy() {
    this.cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
      }
    });
    this.cleanupFunctions = [];
    if (this.emblaApi) {
      try {
        this.emblaApi.destroy();
      } catch (error) {
      }
      this.emblaApi = null;
    }
  }
}
if (!customElements.get("article-slideshow")) {
  customElements.define("article-slideshow", ArticleSlideshow);
}
